// Client-side "break it" transforms.
//
// A transform takes a receipt package's file map and returns a MUTATED COPY
// (the original is never touched) that makes verification fail in a specific,
// honest way. The demo re-runs the real verifier on the result and the
// verification panel shows the check that now fails — the verifier catching
// tampering, live, in the visitor's own browser.
//
// Two kinds:
//   - "tamper-evidence": flip one character in a recorded evidence value so
//     its committed hash no longer matches. hash_integrity fails. No re-seal:
//     the whole point is that a post-seal edit is detectable.
//
//   - "tamper-schema": do NOT touch the package (editing it would fail the
//     content hash first) and do NOT re-seal. Instead alter the JSON Schema
//     the verifier LOADS. The receipt committed to the schema's hash, so when
//     the verifier recomputes the hash of the altered schema it no longer
//     matches and schema_valid fails while hash_integrity stays intact. The
//     override is delivered through the virtual FS (browser) / a temp registry
//     (tests); breakLayer leaves the file map unchanged for this kind and the
//     override is produced by makeSchemaOverride / tamperSchema below.

export type BreakKind = "tamper-evidence" | "tamper-schema";

type Files = Map<string, string>;

export function breakLayer(files: Files, kind: BreakKind): Files {
  const out: Files = new Map(files);
  switch (kind) {
    case "tamper-evidence":
      return tamperEvidence(out);
    case "tamper-schema":
      // The schema break does not edit the package. It substitutes the schema
      // the verifier loads (see makeSchemaOverride). Return the copy untouched
      // so hash_integrity stays PASS and only schema_valid fails.
      return out;
    default:
      return out;
  }
}

// ---------------------------------------------------------------------------
// tamper-schema: substitute the schema the verifier loads, not the package.
// ---------------------------------------------------------------------------

// The evidence type whose schema we tamper with, and the EXACT path the
// verifier reads that schema from. Confirmed empirically with a probe: in the
// browser PROJECT_ROOT resolves to "", so schema-registry's resolveAsset turns
// the manifest path "schemas/action/policy_checked.v0.1.schema.json" into that
// same literal string, which is the key the virtual-fs readFile receives.
export const TAMPERED_SCHEMA_ID = "sequesign.policy_checked.v0.1";
export const TAMPERED_SCHEMA_PATH = "schemas/action/policy_checked.v0.1.schema.json";

// Return a mutated copy of a schema whose canonical bytes differ from the
// original, so its recomputed sha256 no longer matches the hash the receipt
// committed. The change is a genuine rule change (a tightened bound plus an
// added marker property) — but the specific change is immaterial: the verifier
// rejects on the schema-HASH mismatch before it ever evaluates the new rules.
export function tamperSchema(schema: Record<string, unknown>): Record<string, unknown> {
  const mutated = JSON.parse(JSON.stringify(schema)) as Record<string, unknown>;
  const props = mutated.properties;
  if (props && typeof props === "object" && !Array.isArray(props)) {
    const p = props as Record<string, unknown>;
    // Tighten an existing constraint so this reads as an altered rule.
    const limit = p.auto_pay_limit_usd;
    if (limit && typeof limit === "object" && !Array.isArray(limit)) {
      (limit as Record<string, unknown>).minimum = 1_000_000;
    }
    // And add a property, guaranteeing the bytes differ regardless of shape.
    p.tampered_marker = { type: "string" };
  }
  // Belt-and-suspenders: a top-level marker so the hash differs even if the
  // schema had no `properties` object to mutate.
  mutated["x_schema_tampered"] = true;
  return mutated;
}

export interface SchemaOverride {
  path: string;
  contents: string;
}

// Build the virtual-FS override the browser demo registers before verifying:
// the tampered policy_checked schema at the exact path the verifier reads.
export function makeSchemaOverride(realSchema: Record<string, unknown>): SchemaOverride {
  return {
    path: TAMPERED_SCHEMA_PATH,
    contents: JSON.stringify(tamperSchema(realSchema), null, 2)
  };
}

// ---------------------------------------------------------------------------
// tamper-evidence: modify a recorded evidence value without re-sealing.
// ---------------------------------------------------------------------------

function tamperEvidence(files: Files): Files {
  const evidenceKeys = [...files.keys()].filter((k) => k.startsWith("evidence/")).sort();
  for (const key of evidenceKeys) {
    const raw = files.get(key);
    if (raw === undefined) continue;
    let blob: unknown;
    try {
      blob = JSON.parse(raw);
    } catch {
      continue;
    }
    if (mutateFirstLeaf(blob)) {
      files.set(key, JSON.stringify(blob, null, 2));
      return files;
    }
  }
  // Fallback: no mutable evidence blob — flip a value inside actions.jsonl.
  const actionsText = files.get("actions.jsonl");
  if (actionsText !== undefined) {
    const lines = actionsText.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length > 0) {
      const action = JSON.parse(lines[0]);
      if (mutateFirstLeaf(action)) {
        lines[0] = JSON.stringify(action);
        files.set("actions.jsonl", lines.join("\n") + "\n");
      }
    }
  }
  return files;
}

// Depth-first: append a character to the first string leaf, or bump the
// first number leaf. Returns true once one leaf was changed. Keeps the
// document structurally loadable so the failure is a hash mismatch, not a
// parse crash. Prefers a leaf inside a `content` object so the change is in
// meaningful evidence rather than a schema-version string.
function mutateFirstLeaf(node: unknown): boolean {
  if (node !== null && typeof node === "object" && !Array.isArray(node)) {
    const obj = node as Record<string, unknown>;
    if (obj.content !== undefined && mutateFirstLeaf(obj.content)) return true;
    for (const key of Object.keys(obj)) {
      if (key === "content") continue;
      const v = obj[key];
      if (typeof v === "string") {
        obj[key] = v + "X";
        return true;
      }
      if (typeof v === "number") {
        obj[key] = v + 1;
        return true;
      }
      if (v !== null && typeof v === "object" && mutateFirstLeaf(v)) return true;
    }
  }
  return false;
}
